import React, { createContext, useContext, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface ReportFiltersContextType {
    filterParams: string;
    setFilterParams: (params: string) => void;
    clearFilterParams: () => void;
    activeAccordion: number | null;
    setActiveAccordion: (id: number | null) => void;
}

const ReportFiltersContext = createContext<ReportFiltersContextType | null>(null);

export const useReportFilters = () => {
    const context = useContext(ReportFiltersContext);
    if (!context) {
        throw new Error('useReportFilters must be used within a ReportFiltersProvider');
    }
    return context;
};

const ReportFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [filterParams, setFilterParamsState] = useState<string>('');
    const [activeAccordion, setActiveAccordionState] = useState<number | null>(null);

    const setFilterParams = useCallback((params: string) => {
        setFilterParamsState(params);
    }, []);

    const clearFilterParams = useCallback(() => {
        setFilterParamsState('');
        setActiveAccordionState(null);
    }, []);

    const setActiveAccordion = useCallback((id: number | null) => {
        setActiveAccordionState(id);
    }, []);

    return (
        <ReportFiltersContext.Provider value={{ filterParams, setFilterParams, clearFilterParams, activeAccordion, setActiveAccordion }}>
            {children}
        </ReportFiltersContext.Provider>
    );
};

/**
 * Clears a report form's saved filters: the remembered query string in context
 * and the same params in the URL. Without the URL half, each accordion's
 * "populate from URL" effect would refill the form the moment any of its
 * option lists resolved. The `view` param is kept so clearing filters does not
 * bounce the user back to the Advanced Searches tab.
 */
export const useClearReportFilterParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { setFilterParams } = useReportFilters();

    return useCallback(() => {
        setFilterParams('');
        const next = new URLSearchParams();
        const view = searchParams.get('view');
        if (view) next.set('view', view);
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, setFilterParams]);
};

export default ReportFiltersProvider;
